import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import type { Post, PostStatus } from "../lib/types";
import { fullDate, timeAgo } from "../lib/format";
import { useScope } from "../scope/ScopeContext";

const STATUS_FILTERS: { value: PostStatus; label: string }[] = [
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "blocked_compliance", label: "Blocked" },
  { value: "failed", label: "Failed" },
  { value: "skipped", label: "Skipped" },
];

const STATUS_STYLE: Record<PostStatus, string> = {
  draft: "bg-slate-100 text-slate-700 ring-slate-200",
  scheduled: "bg-sky-50 text-sky-700 ring-sky-200",
  publishing: "bg-amber-50 text-amber-700 ring-amber-200",
  published: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  failed: "bg-rose-50 text-rose-700 ring-rose-200",
  blocked_compliance: "bg-rose-50 text-rose-700 ring-rose-200",
  skipped: "bg-slate-50 text-slate-500 ring-slate-200",
};

/**
 * A calendar is a grid, not a list, so it doesn't page — it loads what fits and
 * says so when that isn't everything. `total` is what makes "says so" possible.
 */
const PAGE_LIMIT = 500;

interface PostsResponse {
  posts: Post[];
  total?: number;
  hasMore?: boolean;
}

export function CalendarPage() {
  const { dealers, focusedDealerId } = useScope();
  const [posts, setPosts] = useState<Post[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeStatuses, setActiveStatuses] = useState<Set<PostStatus>>(
    () => new Set<PostStatus>(["scheduled", "published", "draft", "blocked_compliance"])
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      for (const s of activeStatuses) params.append("status", s);
      // The dealer focus goes to the server. It used to be applied here, over
      // the page that had already arrived, so the focused store's posts could
      // fall past the limit and simply not exist on the calendar.
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      params.set("limit", String(PAGE_LIMIT));
      const postsRes = await apiGet<PostsResponse>(`/api/posts?${params.toString()}`);
      setPosts(postsRes.posts);
      setTotal(postsRes.total ?? postsRes.posts.length);
    } catch (err) {
      console.error("posts load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [activeStatuses, focusedDealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Kill switch only makes sense for a single dealer. Show it when focused OR when the
  // user has exactly one dealer in scope.
  const scopedDealer = useMemo(() => {
    if (focusedDealerId) return dealers.find((d) => d.id === focusedDealerId) ?? null;
    if (dealers.length === 1) return dealers[0];
    return null;
  }, [dealers, focusedDealerId]);

  const toggleKillSwitch = async () => {
    if (!scopedDealer) return;
    const want = !scopedDealer.autoPublishEnabled;
    const verb = want ? "ENABLE auto-publish" : "DISABLE auto-publish";
    if (!window.confirm(`${verb} for ${scopedDealer.name}?`)) return;
    try {
      await apiPost(`/api/dealers/${scopedDealer.id}/kill-switch`, { enabled: want });
      await load();
    } catch (err) {
      alert(`Toggle failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const grouped = useMemo(() => groupByDay(posts), [posts]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-8">
      <h1 className="text-2xl font-semibold text-slate-900">Content calendar</h1>
      <p className="mt-1 text-sm text-slate-500">
        Scheduled and published posts to Google Business Profile and Facebook Pages.
      </p>

      {scopedDealer && (
        <div
          className={`mt-6 flex items-center justify-between rounded-xl border px-5 py-4 ${
            scopedDealer.autoPublishEnabled
              ? "border-emerald-200 bg-emerald-50"
              : "border-rose-200 bg-rose-50"
          }`}
        >
          <div>
            <div className="text-sm font-semibold text-slate-900">
              Auto-publish: {scopedDealer.autoPublishEnabled ? "Enabled" : "Disabled"}
            </div>
            <div className="text-xs text-slate-600">
              {scopedDealer.name} · {scopedDealer.autoPublishEnabled
                ? "Scheduled posts will publish automatically."
                : "No posts will publish until re-enabled."}
            </div>
          </div>
          <button
            onClick={() => void toggleKillSwitch()}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium text-white ${
              scopedDealer.autoPublishEnabled
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {scopedDealer.autoPublishEnabled ? "Disable" : "Enable"}
          </button>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((f) => {
          const active = activeStatuses.has(f.value);
          return (
            <button
              key={f.value}
              onClick={() =>
                setActiveStatuses((prev) => {
                  const next = new Set(prev);
                  if (next.has(f.value)) next.delete(f.value);
                  else next.add(f.value);
                  return next;
                })
              }
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                active
                  ? "bg-brand-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 space-y-6">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {/* A calendar can't page, so when it can't show everything it has to
            say which end it kept — otherwise it reads as a complete month. */}
        {!loading && total > posts.length && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
            Showing the {posts.length} most recent of <strong>{total}</strong> posts
            matching these filters. Narrow the status filters or focus a single
            store to see the rest.
          </div>
        )}
        {!loading && grouped.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-500">
            No posts match the selected filters.
            <p className="mt-1 text-xs">
              Templates compose into posts via the scheduler worker (Phase 2 build-out, not in this commit).
            </p>
          </div>
        )}
        {grouped.map(({ dayKey, items }) => (
          <DayGroup key={dayKey} dayLabel={dayKey} posts={items} />
        ))}
      </div>
    </div>
  );
}

function DayGroup({ dayLabel, posts }: { dayLabel: string; posts: Post[] }) {
  return (
    <section>
      <h3 className="sticky top-0 z-10 -mx-2 bg-slate-50/95 px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 backdrop-blur">
        {dayLabel}
      </h3>
      <ul className="space-y-2">
        {posts.map((p) => (
          <PostCard key={p.id} post={p} />
        ))}
      </ul>
    </section>
  );
}

function PostCard({ post }: { post: Post }) {
  const when = post.publishedAt ?? post.scheduledAt;
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${STATUS_STYLE[post.status]}`}
            >
              {post.status.replace("_", " ")}
            </span>
            <span className="text-xs uppercase text-slate-500">
              {post.targetPlatforms.join(" + ")}
            </span>
            <span className="text-xs text-slate-400">· {post.category}</span>
          </div>
          <p className="mt-2 line-clamp-2 text-sm text-slate-800">{post.copyText}</p>
          {post.ctaLabel && (
            <div className="mt-1 text-xs text-slate-500">
              CTA: <span className="font-medium">{post.ctaLabel}</span>
              {post.ctaUrl && (
                <a href={post.ctaUrl} target="_blank" rel="noreferrer" className="ml-1 text-brand-600 hover:underline">
                  →
                </a>
              )}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right text-xs text-slate-500">
          {when ? (
            <>
              <div className="font-medium text-slate-700">
                {new Date(when).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </div>
              <div title={fullDate(when)}>
                {post.status === "published" ? `${timeAgo(when)}` : `→ ${timeAgo(when)}`}
              </div>
            </>
          ) : (
            <span>unscheduled</span>
          )}
        </div>
      </div>
    </li>
  );
}

function groupByDay(posts: Post[]): { dayKey: string; items: Post[] }[] {
  const groups = new Map<string, Post[]>();
  for (const p of posts) {
    const ts = p.publishedAt ?? p.scheduledAt;
    const key = ts ? new Date(ts).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" }) : "Unscheduled";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(p);
  }
  return Array.from(groups.entries()).map(([dayKey, items]) => ({ dayKey, items }));
}
